import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import classNames from 'classnames';
import CardTitleBox from '@home/pages/studio/components/CardTitleBox';
import { useIntl } from 'umi';
import { LANGUAGE_EN, SYSTEM_THEME_DARK } from '@home/_config/config';
import { getName } from '@home/utils/common';
import GTDRTable from '../GTDRTable';
import { getGameHeader, getGameScoreDom } from './getRender';
import LevelSvg from '@/pages/dashboard/assets/svg/benchmark-level.svg';
import CommonBubble from '@home/components/CommonBubble';
import { isEqual } from 'lodash-es';
import './index.scss';
import { ThemeContext } from '@home/wrappers/common';
import StudioAssessneRatingBar from '@home/pages/studio/studioAssessment/components/StudioAssessneRatingBar';
import { IVersionCol } from './type';

interface IGTDRTableCardProps extends BaseComponentProps {
  activeTabs: string[];
  [key: string]: any;
}

const GTDRTableCard = (props: IGTDRTableCardProps) => {
  const {
    style,
    className,
    titleEn,
    titleCn,
    height = 1000,
    data,
    performanceData,
    performanceLoading,
    performanceError,
    filterValue,
    benchmarkLevel,
    colorLists,
    latestVersionObj,
    gameName,
    gameCover,
    checkList,
    benchmarkConfig,
  } = props;

  const { commonData, gameCode } = data || {};
  const { data: gameData, errorMsg, loading } = commonData || {};
  const { themeValue } = useContext(ThemeContext);
  const isDark = themeValue === SYSTEM_THEME_DARK;
  const colorList = isDark? colorLists?.dark : colorLists?.light;
  const {
    description_en: latestDescEn,
    description_cn: latestDescCn,
    version: latestVersion,
  } = latestVersionObj || {};

  const { version = [] } = filterValue;

  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const title = getName(isEn, titleEn, titleCn);

  const [tableData, setTableData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  // Comparison Overview (详细对比) 旁边的选项，是否展示描述列
  const [showDescription, setShowDescription] = useState(false);
  // 是否展开描述列
  const [descExpanded, setDescExpanded] = useState(false);

  const oldActiveTabs = useRef<string[]>();
  const oldVersionColumns = useRef<any[]>([]);
  const hideVersionKeys = useRef<string[]>([]);

  // 当选中的游戏版本超过 1 时，自动开启描述列展示
  useEffect(() => {
    if ((version?.length || 0) > 1) {
      setShowDescription(true);
    }
  }, [version]);

  const insertDescriptionCols = useCallback((versionCols: IVersionCol[]): IVersionCol[] => {
    const vc = versionCols.filter((col) => !hideVersionKeys.current.includes(col?.key));

    const interleaved: IVersionCol[] = [];
    vc.forEach((versionCol) => {
      interleaved.push(versionCol);
      if (showDescription) {
        interleaved.push({
          key: `__description__${versionCol?.key}`,
          header: (
            <div className={'dbd-benchmark-gtdr-table-description-header'}>
              <div className={'dbd-benchmark-gtdr-table-description-header-title'}>{getName(isEn, 'Description', '描述')}</div>
              <div className={'dbd-benchmark-gtdr-table-description-header-expand'} onClick={() => setDescExpanded((v) => !v)}>
                {descExpanded ? getName(isEn, 'Collapse All', '收起全部') : getName(isEn, 'Expand all', '展开全部')}
              </div>
            </div>
          ),
          render: (_: string, item: Record<string, unknown>) => {
            const html = (item?.[`${versionCol?.key}_desc`] as string) || '-';
            const safe = typeof html === 'string' ? html?.replaceAll('\\n', '<br/>') : '-';
            return (
              <div className="dbd-benchmark-gtdr-table-content-td-div">
                <div
                  className={classNames('dbd-benchmark-gtdr-table-desc', { 
                    'dbd-benchmark-gtdr-table-desc-is-expand': descExpanded,
                    'dbd-benchmark-gtdr-table-desc-is-empty': safe === '-',
                  })}
                  dangerouslySetInnerHTML={{ __html: safe }}
                />
              </div>
            );
          },
        }); 
      }
    });

    return interleaved;
  }, [isEn, showDescription, descExpanded]);

  useEffect(() => {
    const levelColumns =
      checkList?.map((item: any) => {
        const { value, key, nameEn, nameCn } = item;
        // 获取表头注释
        const descObj =
          performanceData.find((item: any) => item?.benchmark_level === key) ||
          {};
        return {
          key,
          header: getGameHeader({
            name: getName(isEn, nameEn, nameCn),
            img: LevelSvg,
            className: 'dbd-benchmark-gtdr-table-game-header-level',
            desc: getName(
              isEn,
              descObj?.benchmark_level_description_en,
              descObj?.benchmark_level_description_cn,
            ),
          }),
          showKey: value,
          render: (value: string, row: any) => (
            <div className="dbd-benchmark-gtdr-table-content-td-div">
              <CommonBubble
                content={
                  row?.[`${key}_desc`] && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: row?.[`${key}_desc`]?.replaceAll(
                          '\\n',
                          '<br/>',
                        ),
                      }}
                    ></div>
                  )
                }
              >
                {value}
              </CommonBubble>
            </div>
          ),
        };
      }) || [];

    const newColumns: IVersionCol[] = [
      {
        key: 'name',
        render: (value: string, row: any) => (
          <div className="dbd-benchmark-gtdr-table-content-td-div">
            <CommonBubble
              content={
                row?.name_desc && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: row?.name_desc?.replaceAll('\\n', '<br/>'),
                    }}
                  ></div>
                )
              }
            >
              {value}
            </CommonBubble>
          </div>
        ),
      },
    ];

    // checkbox不变时，按照筛选器的版本列逻辑
    if (isEqual(benchmarkLevel, oldActiveTabs.current)) {
      // 最新版本永远在第一列
      const hasLatestVersion = version?.includes(latestVersion);
      let newVersion = [];
      if (hasLatestVersion) {
        newVersion = version.filter((ver: string) => ver !== latestVersion);
        newVersion.unshift(latestVersion);
      } else {
        newVersion = version;
      }

      // 根据筛选器动态生成列
      if (newVersion.length > 0) {
        const versionCols = newVersion.map((version: string) => ({
          key: version,
          header: getGameHeader({
            name: gameName,
            version,
            img: gameCover,
            isLatest: latestVersion === version,
            latestText: getName(isEn, latestDescEn, latestDescCn),
          }),
          render: (value: string, item: any) =>
            getGameScoreDom(value, item, colorList, `${version}_desc`, isDark, benchmarkConfig),
          canClose: newVersion?.length > 1,
          isVersion: true,
        }));

        newColumns.push(...insertDescriptionCols(versionCols));
      }
    } else {
      // 有变化的时候，则按照缓存的版本列
      const cacheVersionCols = oldVersionColumns.current || [];
      newColumns.push(...insertDescriptionCols(cacheVersionCols));
    }

    // 关联checkbox
    const baseCols = levelColumns.filter((item: any) =>
      benchmarkLevel.includes(item.showKey),
    );

    if (baseCols.length > 0) {
      newColumns.push(...baseCols);
    }

    // 记录
    oldActiveTabs.current = benchmarkLevel;
    setColumns(newColumns);
  }, [isEn, version, gameCode, colorList, benchmarkLevel, loading, gameName, gameCover, latestVersion, latestDescEn, latestDescCn, performanceData, checkList, insertDescriptionCols]);

  // 缓存当前版本列
  useEffect(() => {
    oldVersionColumns.current = columns?.filter((item) => item?.isVersion);
  }, [columns]);

  useEffect(() => {
    const baseData = {};
    const mergeData = [...(gameData || []), ...(performanceData || [])];
    // 分类组装数据
    mergeData.forEach((item) => {
      const {
        category,
        category_cn,
        category_en,
        index,
        index_cn,
        index_en,
        value,
        benchmark_level,
        version,
        description_cn,
        description_en,
        index_description_en,
        index_description_cn,
      } = item;

      // 表格行数据
      const obj = {
        key: index,
        value,
        name: getName(isEn, index_en, index_cn),
        level: benchmark_level,
        version,
        nameDesc: getName(isEn, index_description_en, index_description_cn),
        valueDesc: getName(isEn, description_en, description_cn),
      };

      if (baseData[category]) {
        baseData[category].children.push(obj);
      } else {
        baseData[category] = {
          title: getName(isEn, category_en, category_cn),
          isOpen: true,
          children: [obj],
        };
      }
    });
    const list = Object.keys(baseData).map((key) => {
      const item = baseData[key] || {};
      const { children } = item;
      const childObj = {};

      children.forEach((child: any) => {
        const { key, level, value, name, version, nameDesc, valueDesc } = child;
        // 根据level或者version作为key值
        const index = version || level;
        if (childObj[key]) {
          childObj[key][index] = value;
          childObj[key][`${index}_desc`] = valueDesc;
        } else {
          childObj[key] = {
            [index]: value,
            [`${index}_desc`]: valueDesc,
            name,
            name_desc: nameDesc,
          };
        }
      });

      return {
        ...item,
        children: Object.keys(childObj).map((key) => childObj[key]),
      };
    });

    setTableData(list);
  }, [performanceData, data, isEn]);

  const closeGame = (item: any) => {
    // 同时移除配对的描述列 __description__${versionKey}
    const descKey = `__description__${item?.key}`;
    let columnList = columns.filter((col) => col?.key !== item?.key && col?.key !== descKey);

    const versionColumns = columnList.filter((col) => col?.isVersion);
    // 最后一列版本不允许删除
    if (versionColumns.length === 1) {
      columnList = columnList.map((item) => {
        if (item.isVersion) {
          return {
            ...item,
            canClose: false,
          };
        }
        return item;
      });
    }
    setColumns(columnList);
    hideVersionKeys.current.push(item?.key);
  };

  // 版本列变化时，清空隐藏版本列
  useEffect(() => {
    hideVersionKeys.current = [];
  }, [version]);

  return (
    <CardTitleBox
      title={
        <div className={'dbd-benchmark-gtdr-table-card-title'}>
          <span>{title}</span>
          <label className={'dbd-benchmark-gtdr-table-card-title-checkbox'}>
            <input
              type="checkbox"
              checked={showDescription}
              onChange={() => {
                setShowDescription(!showDescription);
              }}
              className={'dbd-benchmark-gtdr-table-card-title-checkbox-input'}
            />
            {getName(isEn, 'Show Description', '显示描述')}
          </label>
        </div>
      }
      className={classNames(
        'dbd-benchmark-gtdr-table-card-container dbd-benchmark-gtdr-table-with-description',
        { 'is-zh': !isEn },
        className,
      )}
      style={style}
      normalConfig={{
        isLoading: loading || performanceLoading,
        isError: !!errorMsg || performanceError,
        isNoData: gameData?.length === 0 && performanceData?.length === 0,
        style: { height },
      }}
      rightSolt={
        <StudioAssessneRatingBar
          colorList={colorList}
          colorTextList={[formatMessage({id: 'benchmark_table_high_risk'}), formatMessage({id: 'benchmark_table_medium_risk'}), formatMessage({id: 'benchmark_table_low_risk'})]}
          textList={['0', '3', '6', '10']}
          preText={formatMessage({id: 'benchmark_table_benchmark'})}
          style={{ margin: '20px 8px 20px 0' }}
        />
      }
    >
      <GTDRTable
        columns={columns}
        data={tableData}
        setTableData={setTableData}
        closeColumn={closeGame}
      />
    </CardTitleBox>
  );
};

export default GTDRTableCard;
